// =====================================================
// LogoutModal.tsx
// Rôle : gère l'intégralité du parcours de déconnexion.
//   1) Premier modal : proposition de mémoriser le compte (avatar, nom,
//      refresh_token) pour une reconnexion rapide plus tard
//   2) Second modal : confirmation de déconnexion classique
//   3) Sur confirmation : appel API /logout + sauvegarde éventuelle du
//      compte + nettoyage de la session active (harmonia_session)
//   4) Ce composant NE navigue PAS lui-même : il délègue entièrement la
//      redirection au parent via onLogoutSuccess(wantsToRemember).
//      Le parent est seul responsable de choisir /saved-accounts ou
//      /login, pour éviter tout conflit de navigation (deux router.replace
//      concurrents écrasant l'un l'autre).
// =====================================================

import React, { useState } from 'react';
import {
  StyleSheet,
  Text,
  View,
  Modal,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';
import { saveAccount } from '../lib/savedAccounts';

const API_BASE = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com/logout';

// Informations minimales nécessaires pour proposer la mémorisation du compte.
// À transmettre depuis l'écran appelant (ex: profile.tsx), qui les possède déjà.
interface ProfileForMemory {
  id: string;
  nom: string;
  prenom: string;
  avatar_url?: string | null;
}

interface LogoutModalProps {
  visible: boolean;
  onClose: () => void;
  // Appelé une fois la déconnexion (et la sauvegarde éventuelle du compte)
  // terminées. Le booléen indique si l'utilisateur a choisi de mémoriser
  // le compte — c'est au parent de décider où rediriger en fonction.
  onLogoutSuccess: (wantsToRemember: boolean) => void;
  // Props nécessaires pour la mémorisation du compte
  profile?: ProfileForMemory | null;
  refreshToken?: string;
}

export default function LogoutModal({
  visible,
  onClose,
  onLogoutSuccess,
  profile,
  refreshToken,
}: LogoutModalProps) {
  const { t } = useTranslation();
  // Étape courante du parcours : d'abord la mémorisation, puis la confirmation
  const [step, setStep] = useState<'memory' | 'confirm'>('memory');
  // Choix de l'utilisateur à l'étape mémorisation, utilisé après la déconnexion
  const [wantsToRemember, setWantsToRemember] = useState(false);

  const [loading, setLoading] = useState(false);

  // Réinitialise le parcours à chaque ouverture du modal
  React.useEffect(() => {
    if (visible) {
      setStep('memory');
      setWantsToRemember(false);
      setLoading(false);
    }
  }, [visible]);

  // ── Étape 1 : réponse au prompt de mémorisation ────────────────────────────
  const handleChooseRemember = () => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    setWantsToRemember(true);
    setStep('confirm');
  };

  const handleChooseDiscard = () => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    setWantsToRemember(false);
    setStep('confirm');
  };

  // ── Étape 2 : confirmation + déconnexion serveur ───────────────────────────
  const handleCancel = () => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    onClose();
  };

  const handleConfirmLogout = async () => {
    setLoading(true);

    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
    }

    try {
      const sessionStr = await AsyncStorage.getItem('harmonia_session');
      if (!sessionStr) throw new Error(t('logoutModal.errors.sessionNotFound'));
      const session = JSON.parse(sessionStr);
      const access_token: string = session.access_token;

      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token }),
      });

      const data = await response.json();

      if (!data.success) {
        console.error('[LogoutModal] Échec:', data.error);
      }
    } catch (error) {
      console.error('[LogoutModal] Erreur:', error);
    }

    // Selon le choix fait à l'étape mémorisation, on sauvegarde le compte
    // (ou pas) puis on redirige vers l'écran approprié.
    try {
      if (wantsToRemember && profile && refreshToken) {
        await saveAccount({
          user_id: profile.id,
          nom: profile.nom,
          prenom: profile.prenom,
          avatar_url: profile.avatar_url || null,
          refresh_token: refreshToken,
        });
      }
    } catch (error) {
      console.error('[LogoutModal] Erreur mémorisation du compte:', error);
    }

    await AsyncStorage.removeItem('harmonia_session');
    setLoading(false);

    // On laisse le parent gérer toute la navigation (une seule source de
    // vérité pour éviter deux router.replace concurrents).
    onLogoutSuccess(wantsToRemember);
  };

  return (
    <>
      {/* ── Modal 1 : proposition de mémoriser le compte ─────────────────── */}
      <Modal visible={visible && step === 'memory'} animationType="fade" transparent onRequestClose={onClose}>
        <View style={styles.modalContainer}>
          <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={onClose} />

          <View style={styles.modalContent}>
            <View style={styles.iconWrapper}>
              <View style={[styles.iconCircle, styles.iconCircleMemory]}>
                <Ionicons name="bookmark-outline" size={34} color="#7B1FA2" />
              </View>
            </View>

            <Text style={styles.title}>{t('logoutModal.memory.title')}</Text>
            <Text style={styles.message}>
              {t('logoutModal.memory.message')}
            </Text>

            <View style={styles.divider} />

            <View style={styles.actions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={handleChooseDiscard}
                activeOpacity={0.7}
              >
                <Text style={styles.cancelButtonText}>{t('logoutModal.memory.no')}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.memoryButton}
                onPress={handleChooseRemember}
                activeOpacity={0.8}
              >
                <Ionicons name="bookmark" size={18} color="#FFF" />
                <Text style={styles.memoryButtonText}>{t('logoutModal.memory.yes')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Modal 2 : confirmation de déconnexion ────────────────────────── */}
      <Modal visible={visible && step === 'confirm'} animationType="fade" transparent onRequestClose={onClose}>
        <View style={styles.modalContainer}>
          <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={loading ? undefined : onClose} />

          <View style={styles.modalContent}>
            <View style={styles.iconWrapper}>
              <View style={styles.iconCircle}>
                <Ionicons name="log-out-outline" size={36} color="#FF3B30" />
              </View>
            </View>

            <Text style={styles.title}>{t('logoutModal.confirm.title')}</Text>
            <Text style={styles.message}>
              {t('logoutModal.confirm.message')}
            </Text>

            <View style={styles.divider} />

            <View style={styles.actions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={handleCancel}
                disabled={loading}
                activeOpacity={0.7}
              >
                <Text style={styles.cancelButtonText}>{t('logoutModal.confirm.cancel')}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.logoutButton, loading && styles.buttonDisabled]}
                onPress={handleConfirmLogout}
                disabled={loading}
                activeOpacity={0.8}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <>
                    <Ionicons name="log-out-outline" size={18} color="#FFF" />
                    <Text style={styles.logoutButtonText}>{t('logoutModal.confirm.submit')}</Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  modalContent: {
    backgroundColor: '#FFF',
    borderRadius: 24,
    paddingTop: 32,
    paddingBottom: 28,
    paddingHorizontal: 28,
    width: '85%',
    maxWidth: 400,
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.18,
        shadowRadius: 16,
      },
      android: { elevation: 10 },
    }),
  },

  iconWrapper: { marginBottom: 20 },
  iconCircle: {
    width: 76,
    height: 76,
    borderRadius: 38,
    backgroundColor: '#FFF1F0',
    borderWidth: 2,
    borderColor: '#FFCDD2',
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconCircleMemory: {
    backgroundColor: '#F3E8FF',
    borderColor: '#E4C9FF',
  },

  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1A1A1A',
    marginBottom: 10,
    textAlign: 'center',
  },
  message: {
    fontSize: 15,
    color: '#777',
    textAlign: 'center',
    lineHeight: 22,
  },

  divider: {
    height: 1,
    backgroundColor: '#F0F0F0',
    width: '100%',
    marginVertical: 24,
  },

  actions: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E8E8E8',
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#444',
  },
  logoutButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: '#FF3B30',
    gap: 8,
  },
  logoutButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFF',
  },
  memoryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: '#7B1FA2',
    gap: 8,
  },
  memoryButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFF',
  },
  buttonDisabled: {
    opacity: 0.55,
  },
});
